import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  ANY_SPORT_TEAMS_SLUG_CARDS,
  FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { isType } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import type {
  NoCardState_game,
  NoCardState_task,
} from './__generated__/index.graphql.ts';

const TeamWrapper = styled(Vertical)`
  flex: 1;
  border-radius: 16px;
  background: var(--c-nd-50);
  padding: var(--triple-unit) var(--unit);
`;

type Team = NoCardState_game['homeTeam'] | NoCardState_game['awayTeam'];
type TeamProps = { team: Team; to: string };
const Team = ({ team, to }: TeamProps) => {
  return (
    <TeamWrapper gap={2} center>
      <TeamAvatar team={team} />
      <LabelL bold className="text-center">
        {team?.officialName}
      </LabelL>
      <Button size="small" color="quaternary" to={to}>
        <FormattedMessage defaultMessage="Find Cards" />
      </Button>
    </TeamWrapper>
  );
};

type Props = { game: NoCardState_game; task?: NoCardState_task };
export const NoCardState = ({ game, task }: Props) => {
  const redirectOnCreditEligible =
    task &&
    isType(task, 'DecisivePlayerPickerTask') &&
    task.displayedTypedRules.some(rule =>
      isType(rule, 'CardsCountAsFirstOwnerRule')
    );

  const getLink = (team: NoCardState_game['homeTeam']) => {
    if (!team) {
      return '';
    }

    return `${
      isType(team, 'NationalTeam')
        ? generatePath(FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS, {
            countryCode: team.country.slug,
          })
        : generateSportPath(ANY_SPORT_TEAMS_SLUG_CARDS, {
            params: { slug: team.slug },
            sport: game.sport,
          })
    }?${buildFilterQuery({
      [SEARCH_PARAMS.ON_SALE]: true,
      [SEARCH_PARAMS.RARITY]: task?.rarity || Rarity.limited,
      [SEARCH_PARAMS.SORT]: 'Popular Player',
      ...(redirectOnCreditEligible && {
        [SEARCH_PARAMS.CREDIT_ELIGIBLE]: true,
      }),
    })}`;
  };

  return (
    <Horizontal
      style={{ '--size': '80px', alignItems: 'stretch' } as CSSProperties}
      gap={2}
    >
      <Team team={game.homeTeam} to={getLink(game.homeTeam)} />
      <Team team={game.awayTeam} to={getLink(game.awayTeam)} />
    </Horizontal>
  );
};

NoCardState.fragments = {
  game: gql`
    fragment NoCardState_game on AnyGameInterface {
      id
      sport
      homeTeam {
        slug
        officialName
        country {
          slug
        }
        ...TeamAvatar_team
      }
      awayTeam {
        slug
        officialName
        country {
          slug
        }
        ...TeamAvatar_team
      }
    }
    ${TeamAvatar.fragments.team}
  ` as TypedDocumentNode<NoCardState_game>,
  task: gql`
    fragment NoCardState_task on TaskInterface {
      id
      rarity
      ... on DecisivePlayerPickerTask {
        id
        displayedTypedRules {
          id
        }
      }
    }
  ` as TypedDocumentNode<NoCardState_task>,
};
